import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import katex from 'katex';                 // ✅ V8 수식 렌더링
import 'katex/dist/katex.min.css';         // ✅ V8 수식 글꼴/스타일
import { questionAPI, boardAPI, adminAPI } from '../../api';
import { useAuthStore } from '../../store';
import RightPanel from './RightPanel';
import './Exam.css';

interface Props {
  subject: any;
  examType: '필기' | '실기';
  onBack: () => void;
  onModeChange?: (mode: 'normal' | 'mock' | 'wrong') => void;
}

// 과목별 색상 팔레트 (동적 할당)
const COLOR_PALETTE = [
  '#e74c3c', '#e67e22', '#f39c12', '#2ecc71',
  '#3498db', '#9b59b6', '#1abc9c', '#e91e63',
  '#00bcd4', '#ff5722',
];
const subjectColorMap: Record<string, string> = {};
let colorIdx = 0;
const getSubjectColor = (name: string) => {
  if (!name) return '#7f8c8d';
  if (!subjectColorMap[name]) {
    subjectColorMap[name] = COLOR_PALETTE[colorIdx % COLOR_PALETTE.length];
    colorIdx++;
  }
  return subjectColorMap[name];
};

const useResizer = (initialWidth: number, min: number, max: number, direction: 'right' | 'left' = 'right') => {
  const [width, setWidth] = useState(initialWidth);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(initialWidth);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const adjusted = direction === 'right' ? delta : -delta;
      const newWidth = Math.min(max, Math.max(min, startWidth.current + adjusted));
      setWidth(newWidth);
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [min, max, direction]);

  return { width, onMouseDown };
};

const QuestionView: React.FC<Props> = ({ subject, examType, onBack, onModeChange }) => {
  const { user } = useAuthStore();
  // ✅ 모바일 감지
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [questions, setQuestions] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);

  const [curIdx, setCurIdx] = useState<number>(0);
  const [userAns, setUserAns] = useState<Record<number, any>>({});
  const [showAns, setShowAns] = useState<Record<number, boolean>>({});
  const [showExp, setShowExp] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ year: '', round: '', subject_name: '', type: '' });
  const [typeOrder, setTypeOrder] = useState<'asc' | 'desc'>('asc'); // ✅ 유형 드롭다운 목록 가나다 정렬
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null); // ✅ 이미지 별도 창
  const [imgWin, setImgWin] = useState({ x: 0, y: 0, w: 0, h: 0 }); // 이미지 창 위치/크기
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [restBoxes, setRestBoxes] = useState<string[]>([]);
  const [displayBoxes, setDisplayBoxes] = useState<string[]>([]);
  const [restCount, setRestCount] = useState(8);
  const [restInterval, setRestInterval] = useState(300);
  const [fontSize, setFontSize] = useState(17);
  const restScrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const filteredLengthRef = useRef(0);

  // ✅ PgUp/PgDn 키보드 이동 - 이벤트 중복 방지
  useEffect(() => {
    filteredLengthRef.current = filtered.length;
  }, [filtered.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        if (tag === 'BUTTON') (e.target as HTMLElement).blur();
        setCurIdx((i: number) => Math.min(filteredLengthRef.current - 1, i + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        if (tag === 'BUTTON') (e.target as HTMLElement).blur();
        setCurIdx((i: number) => Math.max(0, i - 1));
      }
    };
    // ✅ capture: true - 이벤트 캡처 단계에서 먼저 처리, passive: false - preventDefault 허용
    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  const isFirstRender = useRef(true);

  // ✅ 현재 문제 항목을 사이드바 중앙에 표시 (사이드바 내부만 스크롤)
  useEffect(() => {
    // 첫 마운트 시만 스킵 (curIdx=0에서 → 누르면 정상 동작)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // ✅ 문제 이동 시 해설보기/정답 초기화 (블럭 처리 복원) - listRef 체크 전에 실행
    setShowExp({});
    setShowAns({});

    if (!listRef.current) return;
    const container = listRef.current;
    const items = container.querySelectorAll('.q-list-row');
    const activeEl = items[curIdx] as HTMLElement;
    if (!activeEl) return;
    const containerH = container.clientHeight;
    const itemTop = activeEl.offsetTop - container.offsetTop;
    const itemH = activeEl.clientHeight;
    container.scrollTop = Math.max(0, itemTop - containerH / 2 + itemH / 2);
  }, [curIdx]);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoCanClose, setVideoCanClose] = useState(false);
  const [videoCountdown, setVideoCountdown] = useState(5);

  // ✅ 이미지 별도 창 열기 (기본 위치/크기 지정 후 표시)
  const openImageWindow = (src: string) => {
    if (!src) return;
    const w = Math.min(460, window.innerWidth - 40);
    const h = Math.min(380, window.innerHeight - 140);
    const x = Math.max(10, window.innerWidth - w - 30);
    const y = 90;
    setImgWin({ x, y, w, h });
    setLightboxSrc(src);
  };

  // ✅ 이미지 창 드래그 이동 (헤더 잡고 이동)
  const startImgDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const origX = imgWin.x, origY = imgWin.y;
    const onMove = (ev: MouseEvent) => {
      setImgWin(prev => {
        const nx = origX + (ev.clientX - startX);
        const ny = origY + (ev.clientY - startY);
        // 헤더가 화면 밖(위/좌우)으로 사라지지 않도록 제한
        const clampedX = Math.max(-(prev.w - 120), Math.min(nx, window.innerWidth - 120));
        const clampedY = Math.max(0, Math.min(ny, window.innerHeight - 44));
        return { ...prev, x: clampedX, y: clampedY };
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ✅ 문제 이동 시 이미지 창 자동 닫기
  useEffect(() => { setLightboxSrc(null); }, [curIdx]);

  const openVideo = (url: string) => {
    if (!url?.trim()) return;
    setVideoUrl(url.trim());
    setVideoCanClose(false);
    setVideoCountdown(5);
    let count = 5;
    const timer = setInterval(() => {
      count--;
      setVideoCountdown(count);
      if (count <= 0) {
        clearInterval(timer);
        setVideoCanClose(true);
      }
    }, 1000);
  };

  const getYouTubeId = (url: string) => url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1] || null;
  const sidebar = useResizer(210, 140, 340, 'right');
  const rest = useResizer(220, 160, 500, 'left');
  const rightPanel = useResizer(500, 200, 900, 'left');

  useEffect(() => {
    setLoading(true);
    questionAPI.list({ subject_id: subject.id, exam_type: examType }).then(res => {
      const data = res.data;
      setQuestions(data);

      // 최신 년도/차수 자동 선택 (미지정 제외)
      const validData = data.filter((q: any) => q.year && q.year !== '미지정');
      if (validData.length > 0) {
        const years = Array.from(new Set(validData.map((q: any) => String(q.year)))).filter(Boolean).sort();
        const latestYear = years[years.length - 1] as string;
        const roundsForYear = Array.from(new Set(
          validData.filter((q: any) => String(q.year) === latestYear).map((q: any) => String(q.round))
        )).filter((r: any) => r && r !== '미지정').sort((a: any, b: any) => Number(a) - Number(b));
        const latestRound = roundsForYear[roundsForYear.length - 1] as string;
        setFilters({ year: latestYear, round: latestRound || '', subject_name: '', type: '' });
      } else {
        // 유효한 년도/차수가 없으면 전체 표시
        setFiltered(data);
      }

      setLoading(false);
    });
    adminAPI.restBoxes().then(res => {
      const boxes = res.data.map((b: any) => b.content).filter(Boolean);
      setRestBoxes(boxes);
      setDisplayBoxes([...boxes]);
    }).catch(() => {});
    adminAPI.settings().then(res => {
      setRestCount(res.data.rest_rotation_count ?? 8);
      setRestInterval(res.data.rest_normal_interval ?? 300);
      const key = examType === '필기' ? 'font_size_pilgi_normal' : 'font_size_silgi_normal';
      setFontSize(res.data[key] ?? 17);
    }).catch(() => {});
  }, [subject, examType]);

  useEffect(() => {
    let f = questions;
    if (filters.year) f = f.filter(q => String(q.year) === filters.year);
    if (filters.round) f = f.filter(q => String(q.round) === filters.round);
    if (filters.subject_name) f = f.filter(q => (q.subject_name || '') === filters.subject_name);
    if (filters.type) f = f.filter(q => q.q_type === filters.type);
    if (searchKeyword.trim()) {
      const keywords = searchKeyword.trim().split('+').map((k: string) => k.trim()).filter(Boolean);
      f = f.filter(q => {
        const target = [
          q.question || '',
          q.option1 || '',
          q.option2 || '',
          q.option3 || '',
          q.option4 || '',
          q.answer || '',
          q.explanation || '',
        ].join(' ');
        return keywords.every((kw: string) => target.includes(kw));
      });
    }
    setFiltered(f);
    setCurIdx(0);
  }, [filters, questions, searchKeyword]);

  const q = filtered[curIdx];

  const years = Array.from(new Set(questions.map(q => String(q.year)))).filter(Boolean).sort();
  const rounds = Array.from(new Set(
    questions.filter(q => !filters.year || String(q.year) === filters.year).map(q => String(q.round))
  )).filter(Boolean).sort((a, b) => Number(a) - Number(b));
  const subjectNames = Array.from(new Set(questions.map(q => q.subject_name || ''))).filter(Boolean).sort();
  const types = Array.from(new Set(questions.map(q => q.q_type || ''))).filter(Boolean)
    .sort((a, b) => typeOrder === 'asc' ? a.localeCompare(b, 'ko') : b.localeCompare(a, 'ko'));

  // 자동 로테이션 - 앞에서 rotateCount개 잘라서 뒤에 붙이는 방식
  // [1,2,3,4,5,6,7,8] → [3,4,5,6,7,8,1,2] (rotateCount=2)
  useEffect(() => {
    if (restBoxes.length < 2) return;
    const timer = setInterval(() => {
      setDisplayBoxes(prev => {
        const count = Math.min(restCount, prev.length - 1);
        const removed = prev.slice(0, count);
        return [...prev.slice(count), ...removed];
      });
    }, restInterval * 1000);
    return () => clearInterval(timer);
  }, [restBoxes, restCount, restInterval]);

  // displayBoxes 변경 시 맨 위로 스크롤
  useEffect(() => {
    if (!restScrollRef.current) return;
    restScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [displayBoxes]);

  const loadComments = useCallback(async () => {
    if (!q) return;
    const res = await boardAPI.comments(q.id);
    setComments(res.data);
  }, [q]);

  useEffect(() => {
    if (showComment) loadComments();
  }, [showComment, loadComments]);

  const submitComment = async () => {
    if (!commentText.trim()) return;
    await boardAPI.createComment({ question: q.id, content: commentText });
    setCommentText('');
    loadComments();
  };

  const addWrong = async () => {
    try {
      await questionAPI.addWrong({ question: q.id, exam_type: examType, is_mock: false });
      alert('오답노트에 추가되었습니다.');
    } catch (e: any) {
      alert('이미 추가된 문제입니다.');
    }
  };



  const getLabel = (fq: any, color: string) => {
    const year = fq.year || '';
    const round = fq.round || '';
    const subj = fq.subject_name || '';
    const num = fq.number || '';
    // ✅ 보유 콘텐츠 아이콘 (이미지/동영상/해설/정답)
    const hasImg = !!fq.image || /\[\d+:?img:/i.test((fq.question || '') + (fq.explanation || ''));
    const vid = fq.video || '';
    const hasVideo = !!vid && (vid.includes('http') || vid.includes('youtu') || /\.(mp4|webm|ogg)$/i.test(vid));
    const hasExp = !!(fq.explanation && String(fq.explanation).trim());
    const hasAns = fq.answer !== undefined && fq.answer !== null && String(fq.answer).trim() !== '';
    return (
      <>
        [{year}-{round}]&nbsp;
        <span style={{ color, fontWeight: 700 }}>{subj}</span>
        &nbsp;{num}번
        <span className="q-list-icons" style={{ marginLeft: 6, fontSize: 11, letterSpacing: 1 }}>
          {hasImg && <span title="이미지">🖼️</span>}
          {hasVideo && <span title="동영상">🎬</span>}
          {hasExp && <span title="해설">📄</span>}
          {hasAns && <span title="정답">✅</span>}
        </span>
      </>
    );
  };

  // [Nimg:경로] 형식 파싱
  // 예: [5img:images/Rest_image/a.png] → 원본의 5% 크기
  //     [120img:images/Rest_image/b.png] → 원본의 120% 크기
  const parseImgTag = (content: string): { path: string; scale: number } | null => {
    const match = content.match(/^\[(\d+)img:(.+)\]$/);
    if (match) return { scale: Number(match[1]), path: match[2].trim() };
    return null;
  };

  // 색상 맵
  const COLOR_MAP: Record<string, string> = {
    '빨강': '#e74c3c', '빨간': '#e74c3c', '빨간색': '#e74c3c',
    '파랑': '#3498db', '파란': '#3498db', '파란색': '#3498db',
    '초록': '#27ae60', '초록색': '#27ae60', '녹색': '#27ae60',
    '주황': '#e67e22', '주황색': '#e67e22', '오렌지': '#e67e22',
    '보라': '#9b59b6', '보라색': '#9b59b6',
    '노랑': '#f1c40f', '노란': '#f1c40f', '노란색': '#f1c40f',
    '분홍': '#e91e63', '분홍색': '#e91e63', '핑크': '#e91e63',
    '갈색': '#8b4513', '회색': '#7f8c8d', '그레이': '#7f8c8d',
    '검정': '#000000', '검정색': '#000000', '검은': '#000000',
    'red': '#e74c3c', 'blue': '#3498db', 'green': '#27ae60',
    'orange': '#e67e22', 'purple': '#9b59b6', 'yellow': '#f1c40f',
    'pink': '#e91e63', 'brown': '#8b4513', 'gray': '#7f8c8d',
    'grey': '#7f8c8d', 'black': '#000000',
  };

  // ✅ V8 수식 한 개 ($...$ → KaTeX)
  // 크기를 지정하지 않는다. KaTeX 가 em 단위로 그리므로 바깥 fontSize 를 그대로 따라간다.
  // 폰트 슬라이더를 움직이면 수식도 같이 커지고, 창 크기가 바뀌어도 글자 크기는 안 변한다.
  const renderMath = (tex: string, key: React.Key): React.ReactNode => {
    try {
      const html = katex.renderToString(tex, {
        throwOnError: false,   // 못 읽는 식은 붉게 표시만 되고 화면은 안 죽는다
        displayMode: false,    // 글줄 안에 흐르도록 (가운데 큰 수식 아님)
        strict: false,
      });
      return <span key={key} dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (e) {
      // KaTeX 가 아예 실패하면 원본을 글자 그대로 보여 준다
      return <span key={key}>{'$' + tex + '$'}</span>;
    }
  };

  // ✅ V8 색상 구간처럼 이미 잘려 나온 조각 안에서 $수식$ 만 따로 처리
  //    [파랑]전압은 $V=IR$ 이다[/] 같은 경우에 쓰인다
  const renderWithMath = (text: string, keyBase: string): React.ReactNode => {
    if (!text) return null;
    const bits = text.split(/(\$[^$\n]+\$)/g);
    return bits.map((b, j) =>
      /^\$[^$\n]+\$$/.test(b)
        ? renderMath(b.slice(1, -1), `${keyBase}-m${j}`)
        : <span key={`${keyBase}-t${j}`}>{b}</span>
    );
  };

  // 문제/보기/정답/해설 텍스트에서 [N:img:경로], <br>, _x000d_, [색상]텍스트[/], $수식$ 파싱하여 렌더링
  const renderText = (text: string): React.ReactNode => {
    if (!text) return null;
    // _x000d_, \r 제거
    let cleaned = text.replace(/_x000d_/gi, '').replace(/\r/g, '');
    // [색상]텍스트[/] → 임시 마커로 치환
    // ✅ 여는 태그는 COLOR_MAP에 등록된 색상명일 때만 인식 → 내부에 [kVA],[㎸A],[kW] 등 단위 대괄호가 있어도 안 깨짐
    const colorNames = Object.keys(COLOR_MAP)
      .sort((a, b) => b.length - a.length)
      .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const colorTagRe = new RegExp('\\[(' + colorNames.join('|') + ')\\]([\\s\\S]*?)\\[\\/\\]', 'gi');
    cleaned = cleaned.replace(colorTagRe, (match, color, content) => {
      const code = COLOR_MAP[color.toLowerCase()] || COLOR_MAP[color];
      if (code) return `@@COLOR:${code}@@${content}@@/COLOR@@`;
      return match;
    });
    // ✅ V8 맨 뒤에 $수식$ 추가. 앞의 네 가지는 손대지 않았다.
    const brPattern = new RegExp('(\\[\\d+:img:[^\\]]+\\]|\\[\\d+img:[^\\]]+\\]|<br\\s*\\/?>|@@COLOR:[^@]+@@[^@]*@@\\/COLOR@@|\\$[^$\\n]+\\$)', 'gi');
    const parts = cleaned.split(brPattern);
    return (
      <>
        {parts.map((part, i) => {
          // 이미지
          const imgMatch = part.match(/^\[(\d+):img:(.+)\]$/) || part.match(/^\[(\d+)img:(.+)\]$/);
          if (imgMatch) {
            const scale = Number(imgMatch[1]);
            const path = imgMatch[2].trim();
            const src = path.startsWith('http') ? path : `/images/${path}`;
            // ✅ 모바일에서 이미지 최소 크기 보장
            const imgStyle = isMobile
              ? { width: `${Math.max(scale, 40)}%`, maxWidth: '100%', borderRadius: 4, verticalAlign: 'middle' }
              : { width: `${scale}%`, maxWidth: '100%', borderRadius: 4, verticalAlign: 'middle' };
            return (
              <img key={i} src={src} alt="문제 이미지"
                style={{ ...imgStyle, cursor: 'zoom-in' }}
                title="클릭하면 크게 보기"
                onClick={() => openImageWindow(src)}
                onError={e => (e.currentTarget.style.display = 'none')} />
            );
          }
          // <br>
          if (/^<br\s*\/?>$/i.test(part)) return <br key={i} />;
          // ✅ V8 수식
          if (/^\$[^$\n]+\$$/.test(part)) return renderMath(part.slice(1, -1), i);
          // 색상 태그 (✅ V8 색칠한 구간 안에 수식이 섞여 있어도 그려지도록)
          const colorMatch = part.match(/^@@COLOR:([^@]+)@@(.*)@@\/COLOR@@$/);
          if (colorMatch) return <span key={i} style={{ color: colorMatch[1] }}>{renderWithMath(colorMatch[2], `c${i}`)}</span>;
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  };

  const renderRestContent = (content: string) => {
    if (!content) return null;

    // [Nimg:경로] 형식 처리
    const imgTag = parseImgTag(content);
    if (imgTag) {
      const src = imgTag.path.startsWith('http') ? imgTag.path : `/images/${imgTag.path}`;
      return (
        <div style={{ textAlign: 'center' }}>
          <img
            src={src}
            alt="휴식 이미지"
            style={{ width: `${imgTag.scale}%`, borderRadius: 6 }}
            onError={e => (e.currentTarget.style.display = 'none')}
          />
        </div>
      );
    }

    // YouTube URL
    if (content.includes('youtube.com') || content.includes('youtu.be')) {
      const match = content.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      const videoId = match?.[1];
      if (videoId) {
        return (
          <iframe
            width="100%"
            height={Math.round(rest.width * 0.56)}
            src={`https://www.youtube.com/embed/${videoId}`}
            frameBorder="0" allowFullScreen title="휴식 영상"
          />
        );
      }
    }

    // 일반 이미지 URL
    if (content.startsWith('http') &&
      (content.includes('.png') || content.includes('.jpg') ||
       content.includes('.gif') || content.includes('.webp'))) {
      return <img src={content} alt="휴식" style={{ width: '100%', borderRadius: 6 }}
        onError={e => (e.currentTarget.style.display = 'none')} />;
    }

    // 텍스트
    return <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{content}</div>;
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  // ✅ 0건이어도 사이드바(필터)는 유지 → 본문에만 "없음" 안내 (아래 q-main에서 처리)
  //    유형/년도/차수 조합이 0건이 되어도 종목선택으로 튕기지 않도록 상단 early-return 제거

  const isCorrect = q && userAns[q.id] !== undefined && String(userAns[q.id]) === String(q.answer);

  return (
    <div className="question-layout">
      {/* ✅ 이미지 별도 창 (body로 포탈 → 앱 헤더보다 위, 드래그 이동 + 크기조절, 배경 안 가림) */}
      {lightboxSrc && createPortal(
        <div
          style={{
            position: 'fixed',
            left: imgWin.x, top: imgWin.y,
            width: imgWin.w, height: imgWin.h,
            minWidth: 220, minHeight: 180,
            zIndex: 2147483000,
            background: '#fff',
            border: '2px solid #3498db',
            borderRadius: 8,
            boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column',
            resize: 'both', overflow: 'hidden',
          }}>
          {/* 헤더 (드래그 핸들) */}
          <div
            onMouseDown={startImgDrag}
            style={{
              cursor: 'move', background: '#3498db', color: '#fff',
              padding: '6px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              userSelect: 'none', fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>
            <span>🖼️ 이미지 · 드래그 이동, 우하단 모서리로 크기조절</span>
            <button
              onClick={() => setLightboxSrc(null)}
              style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1, padding: 0, marginLeft: 8 }}>
              ✕
            </button>
          </div>
          {/* 이미지 영역 */}
          <div style={{ flex: 1, overflow: 'auto', background: '#f4f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={lightboxSrc} alt="문제 이미지"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          </div>
        </div>,
        document.body
      )}
      {/* 동영상 팝업 */}
      {videoUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', background: '#000', borderRadius: 12, padding: 10, maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={e => e.stopPropagation()}>
            {/* 닫기 버튼 - 5초 후 활성화 */}
            <button onClick={() => videoCanClose && setVideoUrl(null)}
              style={{ position: 'absolute', top: -16, right: -16, width: 36, height: 36,
                background: videoCanClose ? '#e74c3c' : '#888',
                color: 'white', border: 'none', borderRadius: '50%', fontSize: videoCanClose ? 20 : 13,
                cursor: videoCanClose ? 'pointer' : 'not-allowed', zIndex: 1, fontWeight: 700 }}>
              {videoCanClose ? '✕' : videoCountdown}
            </button>
            {getYouTubeId(videoUrl) ? (
              <iframe width="800" height="450"
                src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}?autoplay=1`}
                frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen style={{ borderRadius: 6, maxWidth: '85vw', maxHeight: '80vh', display: 'block' }} title="동영상" />
            ) : videoUrl.match(/\.(mp4|webm|ogg)$/i) ? (
              <video controls autoPlay style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 6 }}>
                <source src={videoUrl} />
              </video>
            ) : (
              <div style={{ padding: 20, color: 'white', textAlign: 'center' }}>
                <p style={{ marginBottom: 12 }}>외부 링크로 연결됩니다.</p>
                <a href={videoUrl} target="_blank" rel="noreferrer"
                  style={{ color: '#3498db', textDecoration: 'underline', wordBreak: 'break-all' }}>{videoUrl}</a>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 상단 topbar */}
      <div className="q-topbar">
        <div className="q-topbar-left">
          <button className="btn btn-sm q-topbar-back-btn" onClick={onBack}>← 이전</button>
          <div className="q-topbar-info">
            <span className="q-topbar-title">{subject.name} [{examType}]</span>
            <span className="q-topbar-count">전체 {questions.length}문제</span>
          </div>
          <div className="q-topbar-modes">
            <button className="q-mode-btn active">일반</button>
            <button className="q-mode-btn" onClick={() => onModeChange?.('mock')}>모의고사</button>
            <button className="q-mode-btn" onClick={() => onModeChange?.('wrong')}>오답노트</button>
          </div>
        </div>
        <div className="q-topbar-right">
          {/* 폰트 크기 슬라이더 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '3px 8px' }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>🔤 {fontSize}px</span>
            <input type="range" min={12} max={26} value={fontSize}
              onChange={e => setFontSize(Number(e.target.value))}
              style={{ width: 80, accentColor: 'var(--primary)', cursor: 'pointer' }} />
          </div>
          <div className="q-search-box">
            <input
              className="q-search-input"
              placeholder="🔍 검색... (+로 AND조합)"
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setSearchKeyword('')}
            />
            {searchKeyword && <button className="q-search-clear" onClick={() => setSearchKeyword('')}>✕</button>}
            {searchKeyword && <span className="q-search-count">{filtered.length}건</span>}
          </div>
          <span className="q-topbar-user">사용자: <strong>{user?.username}</strong></span>
        </div>
      </div>

      {/* ✅ 모바일 필터바 - topbar 아래, 사이드바 대신 표시 */}
      {isMobile && (
        <div style={{
          display: 'flex', gap: 6, padding: '8px 12px', flexWrap: 'wrap',
          background: 'white', borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <select className="form-select" style={{ flex: 1, minWidth: 80, fontSize: 12, height: 32 }}
            value={filters.year}
            onChange={e => setFilters({...filters, year: e.target.value, round: ''})}>
            <option value="">년도</option>
            {years.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select className="form-select" style={{ flex: 1, minWidth: 60, fontSize: 12, height: 32 }}
            value={filters.round}
            onChange={e => setFilters({...filters, round: e.target.value})}>
            <option value="">차수</option>
            {rounds.map(r => <option key={r} value={r}>{r}차</option>)}
          </select>
          {subjectNames.length > 0 && (
            <select className="form-select" style={{ flex: 1, minWidth: 80, fontSize: 12, height: 32 }}
              value={filters.subject_name}
              onChange={e => setFilters({...filters, subject_name: e.target.value})}>
              <option value="">과목</option>
              {subjectNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {(filters.year || filters.round || filters.subject_name) && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, height: 32 }}
              onClick={() => setFilters({ year: '', round: '', subject_name: '', type: '' })}>
              🔄
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'center', whiteSpace: 'nowrap' }}>
            {filtered.length}문제
          </span>
        </div>
      )}

      {/* 4등분 본문 */}
      <div className="question-body">

        {/* 1. 사이드바 */}
        <aside className="q-sidebar" style={{ width: sidebar.width }}>
          <div className="q-filter-group">
            <div className="q-filter-row">
              <select className="form-select q-filter-select" value={filters.year}
                onChange={e => setFilters({...filters, year: e.target.value, round: '', type: ''})}>
                <option value="">년도 전체</option>
                {years.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
              <select className="form-select q-filter-select" value={filters.round}
                onChange={e => setFilters({...filters, round: e.target.value, type: ''})}>
                <option value="">차수</option>
                {rounds.map(r => <option key={r} value={r}>{r}차</option>)}
              </select>
            </div>
            {subjectNames.length > 0 && (
              <select className="form-select q-filter-select" value={filters.subject_name}
                onChange={e => setFilters({...filters, subject_name: e.target.value, type: ''})}>
                <option value="">과목 전체</option>
                {subjectNames.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {types.length > 0 && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
                <select className="form-select q-filter-select" style={{ flex: 1 }} value={filters.type}
                  onChange={e => setFilters({ year: '', round: '', subject_name: '', type: e.target.value })}>
                  <option value="">유형 전체</option>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button
                  onClick={() => setTypeOrder(o => (o === 'asc' ? 'desc' : 'asc'))}
                  title="유형 목록 가나다 정렬 (오름/내림)"
                  style={{ flexShrink: 0, border: '1px solid var(--border)', background: 'var(--gray-50)',
                    color: 'var(--primary)', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '0 8px', whiteSpace: 'nowrap' }}>
                  {typeOrder === 'asc' ? '가나다 ▲' : '가나다 ▼'}
                </button>
              </div>
            )}
            {(filters.year || filters.round || filters.subject_name || filters.type) && (
              <button className="btn btn-ghost btn-sm" style={{ width: '100%', fontSize: 11 }}
                onClick={() => setFilters({ year: '', round: '', subject_name: '', type: '' })}>
                🔄 초기화
              </button>
            )}
          </div>
          <div className="q-list-label">{filtered.length}문제</div>
          <div className="q-list-items" ref={listRef}>
            {filtered.map((fq, i) => {
              const ans = userAns[fq.id];
              const correct = ans !== undefined && String(ans) === String(fq.answer);
              const wrong = ans !== undefined && !correct;
              const subjectColor = getSubjectColor(fq.subject_name || '');
              return (
                <button key={fq.id}
                  className={`q-list-row ${i === curIdx ? 'active' : ''} ${correct ? 'correct' : ''} ${wrong ? 'wrong' : ''}`}
                  style={{ '--subject-color': subjectColor } as React.CSSProperties}
                  onClick={() => setCurIdx(i)}>
                  {getLabel(fq, subjectColor)}
                </button>
              );
            })}
          </div>
        </aside>

        <div className="q-resizer" onMouseDown={sidebar.onMouseDown} />

        {/* 2. 메인 문제 영역 */}
        <main className="q-main">
          {filtered.length === 0 ? (
            searchKeyword ? (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="icon">🔍</div>
                <p>"{searchKeyword}" 검색 결과가 없습니다.</p>
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}
                  onClick={() => setSearchKeyword('')}>검색 초기화</button>
              </div>
            ) : (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="icon">📭</div>
                <p>해당 조건의 문제가 없습니다.</p>
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}
                  onClick={() => setFilters({ year: '', round: '', subject_name: '', type: '' })}>🔄 필터 초기화</button>
              </div>
            )
          ) : (
            <>
              <div className="q-header">
                <div className="q-info">
                  <span className={`q-type-badge ${examType === '실기' ? 'silgi' : ''}`}>{examType}</span>
                  <span className="q-subject-badge" style={{
                    background: (getSubjectColor(q?.subject_name || '') + '22'),
                    color: getSubjectColor(q?.subject_name || ''),
                  }}>{q?.subject_name || subject.name}</span>
                  <span className="q-meta">No.{q?.number} | 년도: {q?.year} | 차수: {q?.round} | 유형: {q?.q_type || '-'}</span>
                  {q?.video && (q.video.includes('http') || q.video.includes('youtu') || q.video.match(/\.(mp4|webm|ogg)$/i)) && (
                    <span className="q-video-badge" title="동영상 보기"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openVideo(q.video)}>📹</span>
                  )}
                </div>
                <div className="q-progress">{curIdx + 1} / {filtered.length}</div>
              </div>

              <div className="q-content">
                <div className="q-text" style={{ fontSize, lineHeight: 2 }}>{renderText(q?.question || '')}</div>
                {q?.image && (
                  <div className="q-image">
                    <img src={q.image} alt="문제 이미지"
                      style={{ cursor: 'zoom-in' }}
                      title="클릭하면 크게 보기"
                      onClick={() => openImageWindow(q.image)}
                      onError={e => (e.currentTarget.style.display = 'none')} />
                  </div>
                )}
              </div>

              {examType === '필기' && q && (
                <div className="q-options">
                  {[1,2,3,4].map(n => {
                    const optText = q[`option${n}`];
                    if (!optText) return null;
                    const selected = userAns[q.id] === n;
                    const answered = showAns[q.id];
                    const correct = Number(n) === Number(q.answer);
                    return (
                      <button key={n}
                        className={`q-option ${selected ? 'selected' : ''} ${answered && correct ? 'correct' : ''} ${answered && selected && !correct ? 'wrong' : ''}`}
                        onClick={() => { if (showAns[q.id]) return; setUserAns(prev => ({ ...prev, [q.id]: n })); }}>
                        <span className="option-num">{['①','②','③','④'][n-1]}</span>
                        <span className="option-text" style={{ fontSize }}>{renderText(optText)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {examType === '실기' && q && (
                <div className="silgi-answer">
                  <textarea className="form-control" placeholder="답안을 입력하세요..." rows={4}
                    value={userAns[q.id] || ''}
                    onChange={e => setUserAns(prev => ({ ...prev, [q.id]: e.target.value }))}
                    style={{ resize: 'vertical' }} />
                </div>
              )}

              {q && showAns[q.id] && (
                <div className="q-answer-section">
                  <div className="q-answer">
                    <span className="answer-label">✓ 정답</span>
                    <span className="answer-value" style={{ fontSize }}>
                      {examType === '필기' ? `${q.answer}번` : renderText(q.answer)}
                    </span>
                    {examType === '필기' && (
                      <span className={`answer-result ${isCorrect ? 'correct' : 'wrong'}`}>
                        {isCorrect ? '🎉 정답!' : '❌ 오답'}
                      </span>
                    )}
                  </div>
                  {showExp[q.id] && q.explanation && (
                    <div className="q-explanation">
                      <div className="exp-label">📝 해설</div>
                      <div className="exp-text" style={{ fontSize }}>{renderText(q.explanation)}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="q-buttons">
                <button className="btn btn-ghost" onClick={() => setCurIdx((i: number) => Math.max(0, i-1))} disabled={curIdx === 0}>◀ 이전</button>
                <button className="btn btn-primary"
                  onClick={async () => {
                    if (!q) return;
                    const wasShown = showAns[q.id];
                    setShowAns(prev => ({ ...prev, [q.id]: !prev[q.id] }));
                    // ✅ 정답 확인 시 틀렸으면 자동으로 오답노트에 저장
                    if (!wasShown && userAns[q.id] !== undefined) {
                      const correct = String(userAns[q.id]) === String(q.answer);
                      if (!correct) {
                        try {
                          await questionAPI.addWrong({ question: q.id, exam_type: examType, is_mock: false });
                        } catch (e) {} // 이미 있으면 무시
                      }
                    }
                  }}
                  disabled={!q || (userAns[q?.id] === undefined && examType === '필기')}>
                  {q && showAns[q.id] ? '정답 숨기기' : '정답 확인'}
                </button>
                <button className="btn btn-ghost"
                  onClick={() => q && setShowExp(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                  disabled={!q || !showAns[q?.id]}>해설보기</button>
                <button className="btn btn-ghost" onClick={() => setCurIdx((i: number) => Math.min(filtered.length-1, i+1))} disabled={curIdx === filtered.length-1}>다음 ▶</button>
                <button className="btn btn-danger" onClick={addWrong}>❌ 오답</button>
                <button className="btn btn-ghost" style={{ background: '#ede9fe', color: '#6d28d9' }}
                  onClick={() => setShowComment(!showComment)}>💬 함께공부</button>
              </div>

              {showComment && (
                <div className="q-comments">
                  <div className="comments-header">
                    💬 함께 공부해요
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                      {comments.length}/15
                    </span>
                  </div>
                  <div className="comments-list">
                    {comments.length === 0
                      ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>첫 댓글을 남겨보세요!</p>
                      : comments.map((c: any) => (
                        <div key={c.id} className="comment-item">
                          <span className="comment-author">{c.author_name}</span>
                          <span className="comment-date">{c.created_at?.slice(0,10)}</span>
                          <p className="comment-text">{c.content}</p>
                        </div>
                      ))}
                  </div>
                  {/* ✅ 자동 확장 textarea, 최대 15줄 */}
                  {comments.length < 15 ? (
                    <div className="comment-input" style={{ alignItems: 'flex-end' }}>
                      <textarea
                        className="form-control"
                        placeholder="댓글을 입력하세요... (Shift+Enter: 줄바꿈, Enter: 등록)"
                        value={commentText}
                        rows={1}
                        onChange={e => {
                          setCommentText(e.target.value);
                          // 자동 높이 조절
                          e.target.style.height = 'auto';
                          const lineHeight = 22;
                          const maxLines = 15;
                          const scrollH = e.target.scrollHeight;
                          e.target.style.height = Math.min(scrollH, lineHeight * maxLines) + 'px';
                          e.target.style.overflowY = scrollH > lineHeight * maxLines ? 'auto' : 'hidden';
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submitComment();
                          }
                        }}
                        style={{
                          resize: 'none',
                          overflowY: 'hidden',
                          lineHeight: '22px',
                          minHeight: 36,
                        }}
                      />
                      <button className="btn btn-primary btn-sm" onClick={submitComment}>등록</button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
                      💬 이 문제의 댓글이 가득 찼습니다 (15/15)
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>

        {/* ✅ 휴식창 리사이저만 활성화 */}
        <div className="q-resizer-rest" onMouseDown={rest.onMouseDown} />

        {/* 4. 휴식박스 */}
        <aside className="q-rest" style={{ width: rest.width }}>
          <div className="rest-header">
            <span>🎨 휴식</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{displayBoxes.length}개</span>
          </div>
          {/* 세로 스크롤 영역 - 전체 박스 표시 */}
          <div className="rest-scroll" ref={restScrollRef}>
            {restBoxes.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                휴식 콘텐츠가 없습니다
              </div>
            ) : (
              displayBoxes.map((box, i) => (
                <div key={i} className="rest-item">
                  {renderRestContent(box)}
                </div>
              ))
            )}
          </div>
        </aside>

        {/* ✅ 5. 오른쪽 패널 리사이저 - PC만 */}
        {!isMobile && <div className="q-resizer-rest" onMouseDown={rightPanel.onMouseDown} />}

        {/* ✅ 5. 오른쪽 패널 (메모장/파일뷰어) - PC만 */}
        {!isMobile && (
          <RightPanel
            width={rightPanel.width}
            explanationPage={q?.explanation_page ?? null}
            showExplanation={q ? !!showExp[q.id] : false}
            year={q?.year ? Number(q.year) : null}
            round={q?.round ? Number(q.round) : null}
            subjectId={subject?.id ?? null}
            subjectName={examType === '필기' ? (q?.subject_name ?? null) : null}
            examType={examType}
          />
        )}
      </div>

      {/* ✅ 모바일 전용 - RightPanel 버튼 아래 표시 */}
      {isMobile && (
        <div style={{ width: '100%', borderTop: '2px solid var(--border)', marginTop: 8 }}>
          <RightPanel
            width={window.innerWidth}
            explanationPage={q?.explanation_page ?? null}
            showExplanation={q ? !!showExp[q.id] : false}
            year={q?.year ? Number(q.year) : null}
            round={q?.round ? Number(q.round) : null}
            subjectId={subject?.id ?? null}
            subjectName={examType === '필기' ? (q?.subject_name ?? null) : null}
            examType={examType}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionView;
